/**
 * Sidenav Component
 * Handles side navigation with scroll-based section highlighting, smooth scrolling,
 * mobile tab toggling with focus trapping, and dynamic aside height management.
 *
 * @example
 * const sidenav = new Sidenav();
 *
 * @version 2.1.0
 */
class Sidenav {
  /**
   * Shared focusable element selector used for focus trapping and tab management.
   * @static
   * @readonly
   * @type {string}
   */
  static FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(',');

  /**
   * Default configuration.
   * @static
   * @readonly
   * @type {Object}
   */
  static DEFAULT_CONFIG = {
    selectors: {
      navLinks: 'a[href^="#section-"]',
      sectionIndexes: '.section > span',
      mobileActionTogglers: '.mobile-action-togglers button',
      articleTabs: '.article-tab',
    },
    classes: {
      activeNavItem: 'active-nav-item',
      activeSectionItem: 'active-section-item',
    },
    thresholds: {
      headerHeight: 136,
      activationOffset: 50,
    },
    performance: {
      scrollThrottleMs: 25,
      /** Delay after smooth scroll completes before updating active nav state and closing the mobile tab. */
      navClickScrollDelay: 400,
      /** Delay before removing transient tabindex=-1 from a focused section element. */
      tabindexRemoveDelay: 100,
    },
    debug: false,
  };

  /**
   * @param {Object} [config={}] - Configuration overrides. Nested objects are deep-merged,
   *   so passing `{ thresholds: { headerHeight: 80 } }` preserves all other threshold defaults.
   */
  constructor(config = {}) {
    // Deep-merge nested config objects so a partial override (e.g. one threshold key)
    // does not silently drop the sibling defaults from DEFAULT_CONFIG.
    this.config = {
      ...Sidenav.DEFAULT_CONFIG,
      ...config,
      selectors: { ...Sidenav.DEFAULT_CONFIG.selectors, ...config.selectors },
      classes: { ...Sidenav.DEFAULT_CONFIG.classes, ...config.classes },
      thresholds: { ...Sidenav.DEFAULT_CONFIG.thresholds, ...config.thresholds },
      performance: { ...Sidenav.DEFAULT_CONFIG.performance, ...config.performance },
    };

    this.elements = this.cacheElements();
    this.state = {
      currentActiveLink: null,
    };

    this.handleNavClick = this.handleNavClick.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.scrollTimeout = null;
    this.suppressScrollHandler = false;

    this.init();
  }

  /**
   * Caches DOM elements for performance.
   * @returns {Object}
   * @private
   */
  cacheElements() {
    const { selectors } = this.config;

    const sidenavContainer =
      document.querySelector('aside > div > ol') ??
      document.querySelector('aside ol') ??
      document.querySelector('.sidenav ol');

    if (!sidenavContainer) {
      this.log('Warning: Sidenav container not found.', document.querySelectorAll('aside'));
    }

    return {
      navLinks: Array.from(document.querySelectorAll(selectors.navLinks)),
      sectionIndexes: Array.from(document.querySelectorAll(selectors.sectionIndexes)),
      sections: Array.from(document.querySelectorAll('.section')),
      sidenavContainer,
      sidenavScrollParent: this.findScrollParent(sidenavContainer),
    };
  }

  /**
   * Initializes the sidenav component.
   * @private
   */
  init() {
    this.elements.navLinks.forEach(link => {
      link.addEventListener('click', this.handleNavClick);
    });

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();

    this.initMobileActionTogglers();

    // Single keydown listener handles both Tab trapping (when a mobile tab is open)
    // and Escape (to close all mobile tabs). See handleKeydown.
    document.addEventListener('keydown', this.handleKeydown);

    this.initDynamicHeight();
  }

  /**
   * Manages aside height to match the viewport and pins its bottom
   * relative to the space below the article element.
   * @private
   */
  initDynamicHeight() {
    const aside = document.querySelector('aside');
    const article = document.querySelector('article');

    if (!aside || !article) {
      this.log('Warning: Aside or article element not found for height management');
      return;
    }

    const getBottomOffset = () => {
      const articleBottom = article.offsetTop + article.offsetHeight;
      const spaceBelow = document.documentElement.scrollHeight - articleBottom;
      return Math.max(spaceBelow, 32);
    };

    const applyHeight = () => {
      const vh = window.innerHeight;
      aside.style.height = `${vh}px`;
      aside.style.maxHeight = `${vh}px`;
    };

    applyHeight();
    aside.style.bottom = `${getBottomOffset()}px`;

    this.handleResize = () => {
      applyHeight();
      aside.style.bottom = `${getBottomOffset()}px`;
    };

    window.addEventListener('resize', this.handleResize, { passive: true });

    // Observe the article for content/layout changes that affect its height.
    // `attributes` is included because show/hide toggling via class or inline style
    // can change the article's rendered height without adding/removing child nodes.
    const observer = new MutationObserver(() => {
      clearTimeout(this.heightUpdateTimeout);
      this.heightUpdateTimeout = setTimeout(() => {
        applyHeight();
        aside.style.bottom = `${getBottomOffset()}px`;
      }, this.config.performance.tabindexRemoveDelay);
    });

    observer.observe(article, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    this.heightObserver = observer;
  }

  /**
   * Unified keyboard handler for the document.
   *   - Escape: closes all open mobile tabs.
   *   - Tab: traps focus within the active mobile tab (combined with visible togglers).
   * @param {KeyboardEvent} event - Keyboard event from the document keydown listener.
   * @private
   */
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeAllMobileTabs(this.allTogglers, this.allTabs);
      return;
    }

    if (event.key !== 'Tab') return;

    const openTab = this.allTabs.find(tab =>
      tab.classList.contains('active') && !tab.classList.contains('hidden')
    );
    if (!openTab) return;

    const focusables = this.getFocusableElementsInTabAndTogglers(openTab, this.allTogglers);
    if (!focusables.length) return;

    const first = focusables[0];
    const last  = focusables[focusables.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /**
   * Returns focusable elements inside a tab combined with visible togglers.
   * Uses the shared FOCUSABLE_SELECTOR constant.
   * @param {HTMLElement} tab - The tab element to search within.
   * @param {NodeList|Array} togglers - All mobile action toggler buttons.
   * @returns {Array<HTMLElement>}
   * @private
   */
  getFocusableElementsInTabAndTogglers(tab, togglers) {
    const visibleTogglers = Array.from(togglers).filter(t => t.offsetParent !== null);
    const tabFocusables   = Array.from(tab.querySelectorAll(Sidenav.FOCUSABLE_SELECTOR));
    return [...visibleTogglers, ...tabFocusables];
  }

  /**
   * Cached getter for all mobile action togglers.
   * @returns {Array<HTMLElement>}
   */
  get allTogglers() {
    if (!this._cachedTogglers) {
      this._cachedTogglers = Array.from(document.querySelectorAll(this.config.selectors.mobileActionTogglers));
    }
    return this._cachedTogglers;
  }

  /**
   * Cached getter for all article tabs.
   * @returns {Array<HTMLElement>}
   */
  get allTabs() {
    if (!this._cachedTabs) {
      this._cachedTabs = Array.from(document.querySelectorAll(this.config.selectors.articleTabs));
    }
    return this._cachedTabs;
  }

  /**
   * Returns true if any mobile tab is currently open.
   * @returns {boolean}
   */
  get isMobileMenuOpen() {
    if (this._mobileMenuOpenCache === undefined) {
      this._mobileMenuOpenCache = this.allTabs.some(tab =>
        tab.classList.contains('active') && !tab.classList.contains('hidden')
      );
    }
    return this._mobileMenuOpenCache;
  }

  /**
   * Invalidates the cached mobile menu open state.
   * @private
   */
  clearMobileMenuCache() {
    this._mobileMenuOpenCache = undefined;
  }

  /**
   * Extracts the section type from a target ID string.
   * @param {string} targetId - Target element ID (e.g. "section-charts").
   * @returns {string|null}
   */
  getTabTypeFromId(targetId) {
    if (!targetId) return null;
    const match = targetId.match(/^section-([a-z]+)/i);
    return match ? match[1] : targetId;
  }

  /**
   * Updates a toggler's active state without redundant DOM writes.
   * @param {HTMLElement} toggler - The toggler button element.
   * @param {boolean} isActive - Whether the toggler should be active.
   * @private
   */
  setTogglerState(toggler, isActive) {
    const currentState = toggler.getAttribute('aria-expanded') === 'true';
    if (currentState !== isActive) {
      toggler.classList.toggle('active', isActive);
      toggler.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    }
  }

  /**
   * Updates a tab's active/hidden state without redundant DOM writes.
   * @param {HTMLElement} tab - The article tab element.
   * @param {boolean} isActive - Whether the tab should be active.
   * @private
   */
  setTabState(tab, isActive) {
    const currentActive    = tab.classList.contains('active');
    const currentHidden    = tab.classList.contains('hidden');
    const currentAriaHidden = tab.getAttribute('aria-hidden') === 'true';

    if (currentActive !== isActive || currentHidden === isActive || currentAriaHidden === isActive) {
      tab.classList.toggle('active', isActive);
      tab.classList.toggle('hidden', !isActive);
      tab.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      this.clearMobileMenuCache();
    }
  }

  /**
   * Initializes mobile action togglers for article tabs.
   * Escape and Tab handling are registered once in init() via handleKeydown.
   * @private
   */
  initMobileActionTogglers() {
    const togglers = this.allTogglers;
    const tabs     = this.allTabs;

    if (!togglers.length || !tabs.length) {
      this.log('Mobile action togglers or article tabs not found');
      return;
    }

    togglers.forEach(toggler => {
      toggler.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleMobileActionToggle(toggler, togglers, tabs);
      });
    });

    this.log(`Initialized ${togglers.length} mobile action togglers`);
  }

  /**
   * Handles a mobile toggler click: toggles the associated tab and updates nav state.
   * @param {HTMLElement} clickedToggler - The toggler that was clicked.
   * @param {Array<HTMLElement>} allTogglers - All mobile action toggler buttons.
   * @param {Array<HTMLElement>} allTabs - All article tab elements.
   * @private
   */
  handleMobileActionToggle(clickedToggler, allTogglers, allTabs) {
    const isCurrentlyExpanded = clickedToggler.getAttribute('aria-expanded') === 'true';

    allTogglers.forEach(toggler => {
      this.setTogglerState(toggler, toggler === clickedToggler && !isCurrentlyExpanded);
    });

    allTabs.forEach(tab => {
      this.setTabState(tab, !isCurrentlyExpanded && tab.classList.contains(clickedToggler.getAttribute('data-tab')));
    });

    if (!isCurrentlyExpanded) {
      const targetTab = this.findTargetTab(clickedToggler, allTabs);
      if (targetTab) {
        this.focusFirstElementInTab(targetTab);

        const tabType = clickedToggler.getAttribute('data-tab');
        if (tabType) {
          const navLink = this.elements.navLinks.find(link =>
            link.getAttribute('href')?.includes(tabType)
          );
          if (navLink) {
            this.setActiveNav(navLink);
          }
        }
      }
    }

    this.log(`Mobile action toggle: ${clickedToggler.textContent.trim()}`);
  }

  /**
   * Finds the tab associated with a given toggler via its data-tab attribute.
   * @param {HTMLElement} toggler - The toggler whose associated tab to find.
   * @param {Array<HTMLElement>} allTabs - All article tab elements.
   * @returns {HTMLElement|null}
   * @private
   */
  findTargetTab(toggler, allTabs) {
    const targetClass = toggler.getAttribute('data-tab');
    return allTabs.find(tab => tab.classList.contains(targetClass));
  }

  /**
   * Focuses the first focusable element within a tab.
   * Uses the shared FOCUSABLE_SELECTOR constant.
   * @param {HTMLElement} tabElement - The tab element to search within.
   * @private
   */
  focusFirstElementInTab(tabElement) {
    const focusableElements = tabElement.querySelectorAll(Sidenav.FOCUSABLE_SELECTOR);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  /**
   * Closes all mobile tabs and resets toggler states.
   * @param {Array<HTMLElement>} allTogglers - All mobile action toggler buttons.
   * @param {Array<HTMLElement>} allTabs - All article tab elements.
   * @private
   */
  closeAllMobileTabs(allTogglers, allTabs) {
    allTabs.forEach(tab => this.setTabState(tab, false));
    allTogglers.forEach(toggler => this.setTogglerState(toggler, false));
    this.log('All mobile tabs closed');
  }

  /**
   * Handles navigation link clicks: scrolls to the target section and updates active state.
   * The active nav update is deferred by `performance.navClickScrollDelay` to let the
   * smooth scroll animation settle before re-highlighting.
   * @param {MouseEvent} event - Mouse click event.
   * @private
   */
  handleNavClick(event) {
    event.preventDefault();
    const targetId = event.currentTarget.getAttribute('href')?.substring(1);
    if (!targetId) return;

    const targetElement = document.getElementById(targetId);
    if (!targetElement) return;

    const yOffset = this.config.thresholds.headerHeight || 0;
    const y       = targetElement.getBoundingClientRect().top + window.scrollY - yOffset;

    this.suppressScrollHandler = true;
    const clickedLink = event.currentTarget;

    window.scrollTo({ top: y, behavior: 'smooth' });

    setTimeout(() => {
      this.setFocusToElement(targetElement);
      this.setActiveNav(clickedLink);

      // Close the mobile tab if the link was clicked from within one
      const mobileTab = clickedLink.closest('.article-tab');
      if (mobileTab) {
        this.setTabState(mobileTab, false);
        this.allTogglers.forEach(toggler => {
          const tabType = toggler.getAttribute('data-tab');
          if (tabType && mobileTab.classList.contains(tabType)) {
            this.setTogglerState(toggler, false);
            toggler.focus();
          }
        });
      }

      setTimeout(() => {
        this.suppressScrollHandler = false;
      }, this.config.performance.tabindexRemoveDelay);

    }, this.config.performance.navClickScrollDelay);
  }

  /**
   * Scroll event handler. Skips when the mobile menu is open or scroll is suppressed.
   * @private
   */
  handleScroll() {
    if (this.isMobileMenuOpen || this.suppressScrollHandler) return;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.scrollTimeout = null;
      this.findActiveSection();
    }, this.config.performance.scrollThrottleMs);
  }

  /**
   * Finds and activates the nav link whose target section is closest to the scroll threshold.
   * @private
   */
  findActiveSection() {
    const yOffset = this.config.thresholds.headerHeight + this.config.thresholds.activationOffset;
    let bestNavLink  = null;
    let bestDistance = Infinity;

    this.elements.navLinks.forEach(navLink => {
      const href = navLink.getAttribute('href');
      if (!href) return;

      const targetElement = document.getElementById(href.substring(1));
      if (!targetElement) return;

      const rect = targetElement.getBoundingClientRect();
      const distance = Math.abs(rect.top - yOffset);

      if (rect.top >= -200 && rect.top <= window.innerHeight + 200 && distance < bestDistance) {
        bestDistance = distance;
        bestNavLink = navLink;
      }
    });

    if (bestNavLink && bestNavLink !== this.state.currentActiveLink) {
      this.log(`Scroll: activating ${bestNavLink.getAttribute('href')} (distance: ${bestDistance})`);
      this.setActiveNav(bestNavLink);
    }
  }

  /**
   * Scrolls the sidenav's scrollable ancestor to keep the active link visible.
   * Uses the element's own scrollTop instead of scrollIntoView to avoid
   * triggering scroll on the main page.
   * @param {HTMLElement} activeLink - The active navigation link element.
   * @private
   */
  scrollSidenavToVisible(activeLink) {
    const scrollEl = this.elements.sidenavScrollParent;
    if (!scrollEl || !activeLink) return;

    const scrollRect = scrollEl.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();
    const isAbove = linkRect.top < scrollRect.top;
    const isBelow = linkRect.bottom > scrollRect.bottom;

    if (isAbove || isBelow) {
      const targetScrollTop =
        scrollEl.scrollTop +
        (linkRect.top - scrollRect.top) -
        scrollEl.clientHeight / 2 +
        activeLink.offsetHeight / 2;
      scrollEl.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
      this.log(`Scrolled sidenav to show ${activeLink.getAttribute('href')}`);
    }
  }

  /**
   * Walks up the DOM from the given element and returns the first ancestor
   * with a scrollable overflow-y, or null if none is found.
   * @param {HTMLElement|null} element - Starting element for the DOM walk.
   * @returns {HTMLElement|null}
   * @private
   */
  findScrollParent(element) {
    let el = element?.parentElement;
    while (el && el !== document.body) {
      const { overflowY } = getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') return el;
      el = el.parentElement;
    }
    return null;
  }

  /**
   * Sets the given link as the active nav item and syncs all related UI state.
   * @param {HTMLElement} activeLink - The nav link to activate.
   * @private
   */
  setActiveNav(activeLink) {
    if (this.state.currentActiveLink === activeLink) return;
    this.state.currentActiveLink = activeLink;

    const href = activeLink.getAttribute('href');

    this.elements.navLinks.forEach(link => {
      link.classList.toggle(this.config.classes.activeNavItem, link.getAttribute('href') === href);
    });

    this.elements.sectionIndexes.forEach(span => {
      span.classList.remove(this.config.classes.activeSectionItem, 'bg-interactive-hover');
    });

    const targetId = href?.substring(1);
    let section = document.getElementById(targetId);
    while (section && section.tagName !== 'SECTION') {
      section = section.parentElement;
    }
    if (section) {
      const sectionSpan = section.querySelector('span');
      if (sectionSpan) {
        sectionSpan.classList.add(this.config.classes.activeSectionItem, 'bg-interactive-hover');
      }
    }

    const tabType = this.getTabTypeFromId(targetId);
    this.allTogglers.forEach(toggler => {
      this.setTogglerState(toggler, toggler.getAttribute('data-tab') === tabType);
    });

    this.allTabs.forEach(tab => {
      this.setTabState(tab, tab.classList.contains(tabType));
    });

    this.scrollSidenavToVisible(activeLink);
  }

  /**
   * Temporarily focuses an element without triggering page scroll.
   * Assigns a transient tabindex=-1 if the element is not already focusable.
   * @param {HTMLElement} element - The element to focus.
   * @private
   */
  setFocusToElement(element) {
    if (!element) return;
    const hadTabIndex = element.hasAttribute('tabindex');
    if (!hadTabIndex) {
      element.setAttribute('tabindex', '-1');
    }
    element.focus({ preventScroll: true });
    if (!hadTabIndex) {
      setTimeout(() => {
        element.removeAttribute('tabindex');
      }, this.config.performance.tabindexRemoveDelay);
    }
  }

  /**
   * Removes all event listeners and cleans up resources.
   * Safe to call multiple times.
   * @public
   */
  destroy() {
    this.elements?.navLinks.forEach(link => {
      link.removeEventListener('click', this.handleNavClick);
    });

    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('keydown', this.handleKeydown);

    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }

    if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
    if (this.heightUpdateTimeout) clearTimeout(this.heightUpdateTimeout);

    this.heightObserver?.disconnect();

    this.elements = null;
    this.config = null;
    this.state = null;
  }

  /**
   * Merges new configuration values, deep-merging nested objects to preserve sibling defaults.
   * Re-caches DOM elements to pick up any selector changes.
   * @param {Object} newConfig - Configuration values to merge.
   * @public
   */
  updateConfig(newConfig) {
    this.config = {
      ...this.config,
      ...newConfig,
      selectors: { ...this.config.selectors, ...newConfig.selectors },
      classes: { ...this.config.classes, ...newConfig.classes },
      thresholds: { ...this.config.thresholds, ...newConfig.thresholds },
      performance: { ...this.config.performance, ...newConfig.performance },
    };
    this._cachedTogglers = null;
    this._cachedTabs = null;
    this.elements = this.cacheElements();
    this.log('Configuration updated:', newConfig);
  }

  /**
   * Re-caches DOM elements and updates the active section.
   * @public
   */
  refresh() {
    this._cachedTogglers = null;
    this._cachedTabs = null;
    this.elements = this.cacheElements();
    this.findActiveSection();
    this.log('Elements cache refreshed');
  }

  /**
   * Returns a snapshot of the current component state.
   * @returns {Object}
   * @public
   */
  getState() {
    return {
      currentActiveSection: this.state.currentActiveLink?.getAttribute('href')?.substring(1) ?? null,
      scrollY: window.scrollY,
      isMobileMenuOpen: this.isMobileMenuOpen,
    };
  }

  /**
   * Logs debug messages when debug mode is enabled.
   * Uses optional chaining so this is safe to call after destroy().
   * @param {...*} args - Values to log.
   */
  log(...args) {
    if (this.config?.debug) {
      console.log('[Sidenav]', ...args);
    }
  }
}

/**
 * Initializes the Sidenav component when author info is present.
 * @returns {void}
 */
export default {
  init() {
    if (document.querySelector('.author-info')) {
      new Sidenav();
    }
  }
};
